import React from 'react';

import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { CppInvoiceTotals } from '~/services/LuxLoyaltyService.types';

import { currencyCode, formatCurrencyCents, formatSignedPoints } from './invoiceFormat';

interface Props {
  totals: CppInvoiceTotals;
  currency: string | null;
}

/** One labelled figure in the summary strip. */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}

/**
 * Whole-period figures shown above the transactions grid.
 *
 * These are computed by the service across the entire period, not the visible page — a row's cost
 * depends on the other rows landing on its day, so it cannot be totalled per page. They therefore
 * stay put as the user pages through the grid.
 */
export default function InvoiceTotalsSummary({ totals, currency }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap alignItems="flex-start">
        <Figure label="Points issued" value={formatSignedPoints(totals.creditPoints)} />
        <Figure label="Points debited" value={formatSignedPoints(totals.debitPoints)} />
        <Figure label="Points reversed" value={formatSignedPoints(totals.refundPoints)} />
        <Figure label="Net billable points" value={formatSignedPoints(totals.netPoints)} />
        <Figure
          label={`Total cost (${currencyCode(currency)})`}
          value={formatCurrencyCents(totals.amountCents, currency)}
        />

        <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'center' }}>
          {totals.pendingTransactionCount > 0 && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${totals.pendingTransactionCount} pending — figures will still move`}
            />
          )}
          {totals.unpricedNetPoints !== 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${formatSignedPoints(totals.unpricedNetPoints)} points unpriced`}
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
